import { useState, useMemo } from 'react';
import {
  Badge,
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@@/ui/primitives';
import { ChevronsUpDown, Check, Loader2, ChevronLeft, TrashIcon, FileCodeIcon } from 'lucide-react';
import { cn } from '@@/lib/utils';
import { useListItems } from '../../../hooks/use-items';
import { useListEntities, useGetEntity } from '../../../hooks/use-entities';

interface ReferenceValue {
  ref: { eid: string; iid: string };
}

export function ReferenceInput(props: {
  baseEntityId: string;
  value: ReferenceValue;
  onChange: (value: ReferenceValue) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}) {
  const { baseEntityId, value, onChange, disabled = false, label, placeholder = 'Select an item...' } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [currentEntityId, setCurrentEntityId] = useState(baseEntityId);
  const [navigationStack, setNavigationStack] = useState<string[]>([]);

  const { data: currentEntity } = useGetEntity(currentEntityId);
  const { data: entities, isLoading: isLoadingEntities } = useListEntities({ parentId: currentEntityId });
  const { data: items, isLoading: isLoadingItems } = useListItems(currentEntityId);

  const { data: selectedEntity } = useGetEntity(value?.ref?.eid, { enabled: !!value?.ref?.eid });
  const { data: selectedEntityItems } = useListItems(value?.ref?.eid, {
    enabled: !!value?.ref?.eid && !!value?.ref?.iid
  });

  const selectedItem = useMemo(() => {
    if (typeof value !== 'object' || !value?.ref?.iid) return null;
    return selectedEntityItems?.find((i) => i.itemId === value.ref.iid) ?? null;
  }, [value, selectedEntityItems]);

  const isLoading = isLoadingEntities || isLoadingItems;
  const isAtBase = currentEntityId === baseEntityId;

  const filteredEntities = useMemo(() => {
    if (!entities) return [];
    if (!search) return entities;
    return entities.filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));
  }, [entities, search]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    if (!search) return items;
    return items.filter((i) => i.primaryKey.toLowerCase().includes(search.toLowerCase()));
  }, [items, search]);

  const handleNavigateToEntity = (entityId: string) => {
    setNavigationStack((prev) => [...prev, currentEntityId]);
    setCurrentEntityId(entityId);
    setSearch('');
  };

  const handleNavigateBack = () => {
    const prevEntityId = navigationStack[navigationStack.length - 1];
    if (prevEntityId) {
      setNavigationStack((prev) => prev.slice(0, -1));
      setCurrentEntityId(prevEntityId);
      setSearch('');
    }
  };

  const handleSelectItem = (entityId: string, itemId: string) => {
    onChange({ ref: { eid: entityId, iid: itemId } });
    setOpen(false);
    resetNavigation();
  };

  const handleClear = () => {
    onChange({ ref: { eid: '', iid: '' } });
  };

  const resetNavigation = () => {
    setCurrentEntityId(baseEntityId);
    setNavigationStack([]);
    setSearch('');
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetNavigation();
    }
  };

  const getDisplayText = () => {
    if (!selectedItem) return placeholder;
    const entityName = selectedEntity?.name ?? '';
    return entityName ? `${entityName} / ${selectedItem.primaryKey}` : selectedItem.primaryKey;
  };

  return (
    <div className='space-y-2'>
      <div className='flex items-center gap-2'>
        {label && <Label className='text-neutral-800 text-sm font-medium'>{label}</Label>}
        <Badge variant='outline' className='text-slate-800 border-slate-400 text-xs!'>
          Reference
        </Badge>
      </div>
      <div className='flex gap-2 items-center max-w-md'>
        <Popover onOpenChange={handleOpenChange} open={open} modal={true}>
          <PopoverTrigger asChild>
            <Button
              aria-expanded={open}
              className='justify-between text-sm! flex-1'
              role='combobox'
              variant='outline'
              disabled={disabled}
            >
              <span className='truncate'>{getDisplayText()}</span>
              <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='p-0 font-sans w-96' align='start'>
            <Command shouldFilter={false}>
              <CommandInput onValueChange={setSearch} className='text-sm!' placeholder='Search...' value={search} />
              <CommandList>
                {!isAtBase && (
                  <div className='flex items-center border-b px-1 py-1'>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      onClick={handleNavigateBack}
                      className='h-6 px-1.5! text-sm! leading-snug font-[450]! text-muted-foreground '
                    >
                      <ChevronLeft className='size-4' />
                      {currentEntity && <span className='truncate'>{currentEntity.name}</span>}
                    </Button>
                  </div>
                )}
                {isLoading ? (
                  <div className='flex items-center justify-center p-4'>
                    <Loader2 className='size-4 animate-spin' />
                    <span className='ml-2 text-muted-foreground text-sm'>Loading...</span>
                  </div>
                ) : (
                  <>
                    {filteredEntities.length > 0 && (
                      <CommandGroup heading='Sub-Entities'>
                        {filteredEntities.map((entity) => (
                          <CommandItem
                            key={entity.entityId}
                            onSelect={() => handleNavigateToEntity(entity.entityId)}
                            value={entity.name}
                          >
                            <span className='font-medium'>{entity.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {filteredItems.length > 0 && (
                      <CommandGroup heading='Items'>
                        {filteredItems.map((item) => {
                          const isSelected = value?.ref?.eid === currentEntityId && value?.ref?.iid === item.itemId;
                          return (
                            <CommandItem
                              key={item.itemId}
                              onSelect={() => handleSelectItem(currentEntityId, item.itemId)}
                              value={item.primaryKey}
                            >
                              <FileCodeIcon className='size-4 text-blue' />
                              <span className='font-medium flex-1 leading-snug'>{item.primaryKey}</span>
                              <Check className={cn('size-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}
                    {filteredEntities.length === 0 && filteredItems.length === 0 && (
                      <div className='p-4 text-center text-sm text-muted-foreground'>No entities or items found</div>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedItem && (
          <Button variant='ghost' size='sm' onClick={handleClear} disabled={disabled}>
            <TrashIcon className='size-4' />
          </Button>
        )}
      </div>
    </div>
  );
}
